import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { pressKitContent as p } from "../mock";

const PressKit = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background-ivory" data-testid="press-kit-page">
      <Header />
      <main className="flex-grow pt-32 pb-24 px-margin-mobile md:px-margin-desktop">
        {/* Hero */}
        <section className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Reveal
            as="p"
            className="font-inter text-eyebrow text-brand-secondary uppercase tracking-[0.28em] mb-6"
          >
            {p.eyebrow}
          </Reveal>
          <Reveal
            as="h1"
            delay={100}
            className="font-cormorant text-display-lg-mobile md:text-display-lg text-brand-ink mb-8"
          >
            {p.heading}
          </Reveal>
          <Reveal delay={200} className="w-16 h-px bg-brand-gold mx-auto mb-8" />
          <Reveal as="p" delay={300} className="font-cormorant text-body-lg text-deep-taupe">
            {p.intro}
          </Reveal>
        </section>

        {/* Quick Facts */}
        <section className="max-w-5xl mx-auto mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6 text-center border-y border-hairline-beige py-12">
            {p.facts.map((f) => (
              <Reveal key={f.label}>
                <p className="font-cormorant text-[26px] md:text-[32px] text-brand-ink leading-none mb-3">
                  {f.value}
                </p>
                <p className="font-inter text-eyebrow uppercase tracking-[0.25em] text-brand-secondary">
                  {f.label}
                </p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* About + Boilerplate */}
        <section className="max-w-3xl mx-auto mb-20">
          <Reveal
            as="p"
            className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest mb-6"
          >
            About the House
          </Reveal>
          <Reveal
            as="p"
            delay={100}
            className="font-cormorant text-body-lg text-brand-ink mb-10 leading-relaxed"
          >
            {p.about}
          </Reveal>
          <div className="bg-cover-ivory border border-hairline-beige p-8 md:p-10">
            <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mb-4">
              Approved Boilerplate
            </p>
            <p className="font-cormorant text-body-md text-deep-taupe leading-relaxed italic">
              {p.boilerplate}
            </p>
          </div>
        </section>

        {/* Media Assets */}
        <section className="max-w-6xl mx-auto mb-20">
          <Reveal
            as="p"
            className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest text-center mb-10"
          >
            Media Assets
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            {p.assets.map((a, i) => (
              <Reveal key={a.title} delay={i * 100} className="group">
                <a
                  href={a.image}
                  download
                  className="block"
                  data-testid={`press-asset-${i}`}
                >
                  <div className="w-full aspect-[4/3] overflow-hidden mb-4 bg-brand-ink">
                    <img
                      src={a.image}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                    />
                  </div>
                  <p className="font-cormorant text-[22px] text-brand-ink leading-tight">
                    {a.title}
                  </p>
                  <p className="font-inter text-eyebrow uppercase tracking-widest text-brand-secondary mt-1">
                    {a.note}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-3 font-inter text-label-sm uppercase tracking-widest text-deep-taupe group-hover:text-bright-gold-on-dark transition-colors">
                    Download
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Media Enquiries CTA */}
        <section className="max-w-3xl mx-auto text-center">
          <div className="w-full h-px bg-hairline-beige mb-14" />
          <Reveal
            as="p"
            className="font-inter text-eyebrow text-brand-secondary uppercase tracking-widest mb-4"
          >
            {p.contactLabel}
          </Reveal>
          <Reveal as="p" delay={100} className="font-cormorant text-headline-md text-brand-ink mb-8">
            {p.contactNote}
          </Reveal>
          <Reveal delay={200}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-brand-gold px-8 py-4 text-brand-ink hover:bg-brand-ink hover:text-ink-on-dark hover:border-brand-ink transition-all duration-500"
              data-testid="press-contact-cta"
            >
              <span className="font-inter text-label-sm uppercase tracking-widest">
                Reach the House
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PressKit;
